/**
 * Period control for the Teams utilisation figure (CWL-157).
 *
 * The segmented-button look is borrowed from the Gantt toolbar's zoom control so
 * the two read the same, but the range maths lives in `lib/utilisationPeriod`.
 */
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  UTILISATION_PERIODS,
  type UtilisationPeriod,
  type UtilisationRange,
  formatPeriodLabel,
} from '../lib/utilisationPeriod';

export interface UtilisationPeriodSelectorProps {
  period: UtilisationPeriod;
  range: UtilisationRange;
  customFrom: string;
  customTo: string;
  onPeriodChange: (period: UtilisationPeriod) => void;
  onStep: (direction: 1 | -1) => void;
  onCustomFromChange: (value: string) => void;
  onCustomToChange: (value: string) => void;
  /** Hide the resolved range when the caller already prints it elsewhere. */
  showLabel?: boolean;
  className?: string;
}

export function UtilisationPeriodSelector({
  period,
  range,
  customFrom,
  customTo,
  onPeriodChange,
  onStep,
  onCustomFromChange,
  onCustomToChange,
  showLabel = true,
  className = '',
}: UtilisationPeriodSelectorProps) {
  const isCustom = period === 'custom';

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <div className="flex items-center border border-border rounded-md overflow-hidden">
        {UTILISATION_PERIODS.map((option) => (
          <Button
            key={option.value}
            variant="ghost"
            size="sm"
            aria-pressed={period === option.value}
            onClick={() => onPeriodChange(option.value)}
            className={`rounded-none h-8 px-3 ${
              period === option.value
                ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white'
                : ''
            }`}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {/* Stepping a custom range makes no sense — its bounds are typed, not navigated. */}
      {!isCustom && (
        <div className="flex items-center border border-border rounded-md overflow-hidden">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-none h-8 px-2"
            aria-label="Previous period"
            onClick={() => onStep(-1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-none h-8 px-2"
            aria-label="Next period"
            onClick={() => onStep(1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {isCustom && (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label="Utilisation period from"
            value={customFrom}
            max={customTo || undefined}
            onChange={(e) => onCustomFromChange(e.target.value)}
            className="h-8 w-[9.5rem]"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="date"
            aria-label="Utilisation period to"
            value={customTo}
            min={customFrom || undefined}
            onChange={(e) => onCustomToChange(e.target.value)}
            className="h-8 w-[9.5rem]"
          />
        </div>
      )}

      {showLabel && (
        <span className="text-sm text-muted-foreground">{formatPeriodLabel(period, range)}</span>
      )}
    </div>
  );
}
